import InfiniteScroll from 'react-infinite-scroll-component';
import { Fragment, useMemo, useState } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerActivityItem } from '@/modules/issuer-dashboard/components/items/IssuerActivityItem.tsx';
import { useIssuerActivitiesListQuery } from '@/modules/issuer-dashboard/queries/activities.tsx';
import { CustomCheckbox, EmptyListState, Spinner } from 'ui';
import { MyActivitiesListV1SrActivitiesMyGetParams, SortedBy } from 'api';

export type TransactionFilters = Pick<
  MyActivitiesListV1SrActivitiesMyGetParams & { is_deleted?: boolean },
  | 'type'
  | 'status'
  | 'total_to'
  | 'register_class_id'
  | 'completion_date_from'
  | 'completion_date_to'
  | 'requested_at_date_from'
  | 'requested_at_date_to'
  | 'is_deleted'
>;

export interface IssuerActivityTabProps {
  filters: TransactionFilters;
  register_id: number;
}

export const IssuerActivityTab = ({ register_id, filters }: IssuerActivityTabProps) => {
  const [isShowActionFirst, setIsShowActionFirst] = useState(false);
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const {
    data: issuerActivitiesList,
    fetchNextPage,
    hasNextPage,
    isLoading: isIssuerActivitiesLoading,
  } = useIssuerActivitiesListQuery(
    {
      page: 1,
      page_size: 15,
      sorted_by: isShowActionFirst ? SortedBy.Status : SortedBy.RequestedAtDate,
      ...filters,
    },
    false,
    Number(register_id),
  );

  const appliedFiltersLength = useMemo(() => Object.keys(filters).length, [filters]);

  if (isIssuerActivitiesLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-start gap-4">
      <div className="w-full flex items-center justify-between">
        <CustomCheckbox
          label="Show Action required first"
          checked={isShowActionFirst}
          onChange={e => setIsShowActionFirst(e.target.checked)}
          className="select-none"
        />
        <div></div>
      </div>
      <EmptyListState
        title={appliedFiltersLength > 0 ? 'Nothing found' : 'No activities yet'}
        description={
          appliedFiltersLength > 0
            ? 'No entries matching your filters'
            : `Pending ${getRegisterTypeStrings('application')} requests will appear here. ` +
              `However, you can click on Add transaction and add any type of transaction manually including Opening balance for migrating existing shareholders holdings.`
        }
        isLoading={isIssuerActivitiesLoading}
        isSearchUsed={false}
        itemsCount={issuerActivitiesList?.pages?.[0]?.total ?? 0}
      />
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={issuerActivitiesList?.pages.length ?? 0}
        hasMore={hasNextPage || false}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        scrollableTarget="scrollable"
      >
        <div className="w-full flex flex-col gap-2">
          {issuerActivitiesList?.pages?.map(({ data }, i) => (
            <Fragment key={i}>
              {data.map((activity, j) => (
                <IssuerActivityItem
                  {...activity}
                  key={activity.id + activity.source}
                  register_id={register_id}
                  index={j}
                  isDeleted={filters.is_deleted}
                  showArchiveButton={true}
                />
              ))}
            </Fragment>
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
};
